"use client";

import { Cpu } from "lucide-react";
import { lazy, Suspense, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import type { FleetHealth } from "@/lib/devices/fleet-dashboard";
import {
  approximateIndiaCoords,
  clampLatLngToIndia,
  computeFleetHealth,
  daysUntil,
  fleetHealthDotClass,
  fleetHealthLabel,
  readCustomerName,
  readDateOfIssue,
  readDeviceCoordinates,
  readWarrantyEnd,
  warrantyBucket,
} from "@/lib/devices/fleet-dashboard";
import { getDeviceSecondaryIdentifier } from "@/lib/devices/device-field-helpers";
import { listAlerts } from "@/lib/api/endpoints/alerts";
import type { DeviceDto } from "@/lib/api/types";
import { DataPanel } from "@/components/ds/data-panel";
import { EmptyState } from "@/components/ds/empty-state";
import { PageHeader, PageSection, WorkspacePage } from "@/components/ds/workspace-page";
import { FlashToast } from "@/components/ui-system/flash-toast";
import type { FleetKpiCounts, FleetMapMarker, WarrantyRow } from "@/components/devices";
import {
  FleetActivityHub,
  fleetDashboardCardClasses,
  FleetHealthRingsCard,
  FleetThresholdPanel,
  FleetWarrantyPanel,
} from "@/components/devices";
import { SkeletonTableRow } from "@/components/ds/skeleton";
import { StatusIndicator } from "@/components/ui/status-indicator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import { devicesGateway } from "@/modules/devices/api";
import { useRealtimeStore } from "@/store/realtime";
import { cn } from "@/lib/utils/cn";

const mapLoadingFallback = (
  <div className="flex h-[var(--map-height)] items-center justify-center rounded-control border border-card-border bg-surface-primary text-caption text-muted shadow-card">
    Loading map...
  </div>
);

const FleetIndiaMap = lazy(() =>
  import("@/components/devices/fleet-india-map").then((m) => ({ default: m.FleetIndiaMap })),
);

function formatLastSeen(iso: string | null | undefined): string {
  if (!iso) {
    return "-";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString();
}

function rowStatus(
  deviceStatus: string | null | undefined,
  live: string | null | undefined,
): "online" | "offline" | "unknown" {
  const s = (live ?? deviceStatus ?? "unknown").toLowerCase();
  if (s === "online" || s === "active") {
    return "online";
  }
  if (s === "offline" || s === "inactive") {
    return "offline";
  }
  return "unknown";
}

function statusVariantForRow(display: "online" | "offline" | "unknown"): "online" | "offline" | "neutral" {
  if (display === "online") {
    return "online";
  }
  if (display === "offline") {
    return "offline";
  }
  return "neutral";
}

type MergedFleetRow = {
  device: DeviceDto;
  display: "online" | "offline" | "unknown";
  live: string | null;
  health: FleetHealth;
  warrantyEnd: Date | null;
  warrantyBucket: ReturnType<typeof warrantyBucket>;
  warrantyDays: number | null;
  mapLat: number;
  mapLng: number;
  mapApprox: boolean;
  hasMetaCoords: boolean;
};

export function EnterpriseDevicesListPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { permissionMap } = useEffectivePermissions();
  const canCreateDevices = hasModulePermission(user, permissionMap, "devices", "create");
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const statusMap = useRealtimeStore((s) => s.deviceStatus);
  const statusRev = useRealtimeStore((s) => s.deviceStatusRevision);

  const query = useQuery({
    queryKey: ["enterprise-devices", tenantId ?? "", search],
    queryFn: () =>
      devicesGateway.listPage({
        tenantId: tenantId!,
        limit: 200,
        offset: 0,
        search: search.trim() || undefined,
        status: undefined,
      }),
    enabled: Boolean(tenantId),
    staleTime: 20_000,
  });

  const alertsQuery = useQuery({
    queryKey: ["fleet-dashboard-alerts", tenantId ?? ""],
    queryFn: () => listAlerts({ tenantId: tenantId! }),
    enabled: Boolean(tenantId),
    staleTime: 25_000,
  });

  const rows = query.data?.items ?? [];

  const mergedRows: MergedFleetRow[] = useMemo(() => {
    void statusRev;
    const now = Date.now();
    return rows.map((d) => {
      const live = statusMap.get(d.id) ?? null;
      const display = rowStatus(d.status, live);
      const health = computeFleetHealth(d.last_seen, display);
      const metaCoords = readDeviceCoordinates(d.metadata);
      const hasMetaCoords = metaCoords !== null;
      let mapLat: number;
      let mapLng: number;
      let mapApprox: boolean;
      if (metaCoords) {
        const c = clampLatLngToIndia(metaCoords.lat, metaCoords.lng);
        mapLat = c.lat;
        mapLng = c.lng;
        const clamped = c.lat !== metaCoords.lat || c.lng !== metaCoords.lng;
        mapApprox = clamped;
      } else {
        const a = approximateIndiaCoords(d.id);
        mapLat = a.lat;
        mapLng = a.lng;
        mapApprox = true;
      }
      const wEnd = readWarrantyEnd(d.metadata);
      const bucket = warrantyBucket(wEnd, now);
      const warrantyDays = wEnd ? daysUntil(wEnd, now) : null;

      return {
        device: d,
        display,
        live,
        health,
        warrantyEnd: wEnd,
        warrantyBucket: bucket,
        warrantyDays,
        mapLat,
        mapLng,
        mapApprox,
        hasMetaCoords,
      };
    });
  }, [rows, statusMap, statusRev]);

  const kpiCounts: FleetKpiCounts = useMemo(() => {
    const c: FleetKpiCounts = {
      total: mergedRows.length,
      healthy: 0,
      degraded: 0,
      offline: 0,
      unknown: 0,
      onMap: mergedRows.length,
      plottedFromMeta: 0,
      warrantyExpired: 0,
      warranty30: 0,
      warranty90: 0,
      warrantyUnknown: 0,
      thresholdOpen24h: 0,
    };
    const day = 86_400_000;
    const dayAgo = Date.now() - day;
    for (const r of mergedRows) {
      c[r.health] += 1;
      if (r.hasMetaCoords) {
        c.plottedFromMeta += 1;
      }
      switch (r.warrantyBucket) {
        case "expired":
          c.warrantyExpired += 1;
          break;
        case "d30":
          c.warranty30 += 1;
          break;
        case "d90":
          c.warranty90 += 1;
          break;
        case "ok":
          break;
        default:
          c.warrantyUnknown += 1;
      }
    }
    const alerts = alertsQuery.data ?? [];
    c.thresholdOpen24h = alerts.filter((a) => {
      const t = new Date(a.created_at).getTime();
      return t >= dayAgo && a.status !== "resolved";
    }).length;
    return c;
  }, [mergedRows, alertsQuery.data]);

  const mapMarkers: FleetMapMarker[] = useMemo(
    () =>
      mergedRows.map((r) => ({
        id: r.device.id,
        label: r.device.name?.trim() || r.device.device_uid,
        uid: getDeviceSecondaryIdentifier(r.device),
        lat: r.mapLat,
        lng: r.mapLng,
        health: r.health,
        approximate: r.mapApprox,
        lastSeen: r.device.last_seen,
      })),
    [mergedRows],
  );

  const warrantyRows: WarrantyRow[] = useMemo(
    () =>
      mergedRows.map((r) => ({
        id: r.device.id,
        deviceUid: getDeviceSecondaryIdentifier(r.device),
        deviceName: r.device.name?.trim() || r.device.device_uid,
        customerName: readCustomerName(r.device.metadata),
        dateOfIssue: readDateOfIssue(r.device.metadata),
        warrantyExpiry: r.warrantyEnd,
      })),
    [mergedRows],
  );

  const thresholdAlerts = useMemo(
    () =>
      [...(alertsQuery.data ?? [])].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [alertsQuery.data],
  );

  const registryTableHeadClass = "text-sm font-semibold normal-case tracking-tight text-foreground";

  const filteredTableRows = useMemo(() => {
    if (!healthFilter) {
      return mergedRows;
    }
    return mergedRows.filter((r) => r.health === healthFilter);
  }, [mergedRows, healthFilter]);

  function openCreateLogger() {
    if (!canCreateDevices) {
      setToast("You don't have permission to create data loggers.");
      return;
    }
    navigate("/devices/new");
  }

  if (!tenantId) {
    return (
      <WorkspacePage className="pt-0">
        <EmptyState
          title="Authentication required"
          description="Sign in with a workspace-bound user to list data loggers."
          action={
            <Link to="/login" className="btn btn-primary btn-sm !no-underline">
              Sign in
            </Link>
          }
        />
      </WorkspacePage>
    );
  }

  return (
    <WorkspacePage className="pt-0">
      <div className="space-y-ds-6">
        <PageHeader
          kicker="Devices"
          title="Fleet"
          description="Track fleet health, coverage, warranty risk, alert pressure, and registry status in a denser workspace operations view."
          meta={
            <>
              <span>{mergedRows.length} endpoints in scope</span>
              <span className="text-muted/70" aria-hidden>·</span>
              <span>{kpiCounts.thresholdOpen24h} open threshold alerts in the last 24 hours</span>
            </>
          }
          actions={
            <>
              <Link to="/devices" className="btn btn-secondary btn-sm !no-underline">
                Device directory
              </Link>
              <button type="button" onClick={openCreateLogger} className="btn btn-primary btn-sm shrink-0">
                Create data logger
              </button>
            </>
          }
        />

        {query.isLoading ? (
          <div className="grid gap-6 lg:grid-cols-[minmax(280px,360px)_1fr]">
            <div className="h-[var(--live-chart-height)] animate-pulse rounded-control border border-card-border bg-surface-primary shadow-card" />
            <div className="h-[var(--live-chart-height)] animate-pulse rounded-control border border-card-border bg-surface-primary shadow-card" />
          </div>
        ) : query.isError ? null : (
          <div className="grid gap-6 lg:grid-cols-[minmax(280px,360px)_1fr] lg:items-stretch">
            <FleetHealthRingsCard counts={kpiCounts} />
            <FleetActivityHub
              search={search}
              onSearchChange={setSearch}
              healthFilter={healthFilter}
              onHealthFilter={setHealthFilter}
              counts={kpiCounts}
            />
          </div>
        )}

        {!query.isLoading && !query.isError && mergedRows.length > 0 ? (
          <Suspense fallback={mapLoadingFallback}>
            <FleetIndiaMap
              markers={mapMarkers}
              className={cn(fleetDashboardCardClasses, "border-card-border bg-card shadow-card")}
            />
          </Suspense>
        ) : null}

        {!query.isLoading && !query.isError && mergedRows.length > 0 ? (
          <div className="grid gap-6 lg:grid-cols-2">
            <FleetWarrantyPanel rows={warrantyRows} />
            <FleetThresholdPanel alerts={thresholdAlerts} />
          </div>
        ) : null}

        <PageSection>
          <DataPanel
            title="Registry"
            titleClassName="font-sans text-sm font-semibold tracking-tight text-foreground"
            description="Status merges API data with live presence updates. Health uses last-seen windows: healthy within 15 minutes and degraded within 60 minutes."
            flush
            className={fleetDashboardCardClasses}
          >
            {query.isLoading ? (
              <div className="px-ds-4 py-ds-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonTableRow key={i} />
                ))}
              </div>
            ) : query.isError ? (
              <p className="px-ds-6 py-ds-12 text-center text-caption text-error">Registry request failed.</p>
            ) : mergedRows.length === 0 ? (
              <div className="p-ds-4">
                <EmptyState
                  icon={Cpu}
                  title="No data loggers"
                  description="No endpoints match this workspace or filter. Create a logger to start ingesting telemetry."
                  action={
                    <button type="button" onClick={openCreateLogger} className="btn btn-primary btn-sm">
                      Create data logger
                    </button>
                  }
                />
              </div>
            ) : (
              <Table flush>
                <TableHeader>
                  <TableRow>
                    <TableHead className={registryTableHeadClass}>Name</TableHead>
                    <TableHead className={registryTableHeadClass}>Health</TableHead>
                    <TableHead className={registryTableHeadClass}>Status</TableHead>
                    <TableHead className={cn(registryTableHeadClass, "text-right")}>Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTableRows.map((r) => (
                    <TableRow key={r.device.id}>
                      <TableCell>
                        <Link
                          to={`/devices/${r.device.id}`}
                          className="font-medium text-link hover:text-link-hover hover:underline"
                        >
                          {r.device.name?.trim() || r.device.device_uid}
                        </Link>
                        <div className="mt-ds-1 text-micro normal-case tracking-normal text-muted">
                          {getDeviceSecondaryIdentifier(r.device)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-ds-2 font-sans text-caption capitalize text-foreground">
                          <span className={`h-2 w-2 shrink-0 rounded-full ${fleetHealthDotClass(r.health)}`} />
                          {fleetHealthLabel(r.health)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-ds-2 capitalize text-muted">
                          <StatusIndicator
                            variant={statusVariantForRow(r.display)}
                            size="sm"
                            pulse={r.display === "online" && r.live !== null}
                          />
                          {r.display}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted">
                        {formatLastSeen(r.device.last_seen)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DataPanel>
        </PageSection>
      </div>
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </WorkspacePage>
  );
}
